import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import api, { apiError, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

export default function Orders() {
  const { data: orders = [], isLoading } = useQuery({ queryKey: ["my-orders"], queryFn: async () => (await api.get("/orders")).data });
  if (isLoading) return <div className="py-16 text-center text-ink-muted">Chargement…</div>;
  if (orders.length === 0) return <div className="bg-cream border border-line rounded-lg p-12 text-center text-ink-muted">Aucune commande pour l'instant.</div>;

  return (
    <div>
      <h2 className="font-heading text-2xl text-ink mb-5">Mes commandes</h2>
      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.id} className="bg-cream border border-line rounded-lg p-5" data-testid={`order-${o.order_number}`}>
            <div className="flex justify-between items-start flex-wrap gap-2">
              <div>
                <p className="font-heading text-lg text-ink">{o.order_number}</p>
                <p className="text-xs text-ink-muted">{new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
              </div>
              <div className="text-right">
                <Badge className={o.status === "refunded" ? "bg-ink-muted" : "bg-sage"}>{o.status === "refunded" ? "Remboursée" : "Payée"}</Badge>
                <p className="text-lg font-semibold text-ink mt-1">{formatPrice(o.total)}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-line text-sm text-ink-muted">
              {o.items.map((it, i) => <div key={i}>{it.title} — {formatPrice(it.price)}</div>)}
            </div>
            {o.status !== "refunded" && <div className="mt-3"><RefundDialog order={o} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function RefundDialog({ order }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const submit = async () => {
    try { await api.post("/refund-request", { order_id: order.id, reason }); toast.success("Demande de remboursement envoyée."); setOpen(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="border-line text-ink-muted" data-testid={`refund-${order.order_number}`}>Demander un remboursement</Button></DialogTrigger>
      <DialogContent className="bg-sand">
        <DialogHeader><DialogTitle className="font-heading">Demande de remboursement</DialogTitle></DialogHeader>
        <p className="text-sm text-ink-muted">Remboursement possible sous 15 jours, selon les conditions de nos CGV pour les contenus numériques.</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif de votre demande…" className="bg-cream border-line" data-testid="refund-reason" />
        <DialogFooter><Button onClick={submit} className="bg-terracotta hover:bg-terracotta-dark text-white" data-testid="refund-submit">Envoyer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
